import React, { useState, useEffect, useMemo } from "react";
import { intl, fetchData } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { useFormatters } from "../../hooks/useFormater";

/**
 * Generate dynamic institution columns based on max_institutions count
 */
const generateInstitutionColumns = (maxInstitutions, formatters) => {
    const columns = [];

    for (let i = 1; i <= maxInstitutions; i++) {
        const nameLabel = intl.get(`Reports.Pharmacists.Institution${i}Name`).d(`Dispensing Institution ${i}`);
        const cnpjLabel = intl.get(`Reports.Pharmacists.Institution${i}CNPJ`).d(`Inst. ${i} CNPJ`);
        const addressLabel = intl.get(`Reports.Pharmacists.Institution${i}Address`).d(`Inst. ${i} Address`);
        const cityLabel = intl.get(`Reports.Pharmacists.Institution${i}City`).d(`Inst. ${i} City`);
        const stateLabel = intl.get(`Reports.Pharmacists.Institution${i}State`).d(`Inst. ${i} State`);

        columns.push(
            {
                label: nameLabel,
                column_name: `institution_${i}_name`,
                width: "180px",
            },
            {
                label: cnpjLabel,
                column_name: `institution_${i}_cnpj`,
                width: "140px",
                function: (value) => formatters.formatCNPJ(value),
            },
            {
                label: addressLabel,
                column_name: `institution_${i}_address`,
                width: "180px",
            },
            {
                label: cityLabel,
                column_name: `institution_${i}_city`,
                width: "120px",
            },
            {
                label: stateLabel,
                column_name: `institution_${i}_state`,
                width: "60px",
            }
        );
    }

    return columns;
};

export const PharmacistsReportPage = () => {
    const formatters = useFormatters();
    const [maxInstitutions, setMaxInstitutions] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch max_institutions from filter-options
    useEffect(() => {
        fetchData("/reports/pharmacists/filter-options", "GET", true)
            .then((response) => {
                const data = response?.data?.data || response?.data || {};
                const max = data.max_institutions || 1;
                setMaxInstitutions(max);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Failed to fetch filter options:", error);
                setIsLoading(false);
            });
    }, []);

    // Build header dynamically based on max_institutions
    // Sequence follows .ai_docs/reports.md
    const header = useMemo(() => {
        const baseColumns = [
            {
                label: intl.get("Reports.Pharmacists.Name").d("Pharmacist Name"),
                column_name: "name",
                width: "180px",
            },
            {
                label: intl.get("Reports.Pharmacists.CRFUF").d("CRF/UF"),
                column_name: "crf_uf",
                width: "120px",
            },
            {
                label: intl.get("Reports.Pharmacists.Email").d("Email"),
                column_name: "email",
                width: "250px",
            },
            {
                label: intl.get("Reports.Pharmacists.Phone").d("Phone"),
                column_name: "phone",
                width: "120px",
                function: (value) => formatters.formatPhone(value),
            },
            {
                label: intl.get("Reports.Pharmacists.TrainingDateInitial").d("Initial Training Date"),
                column_name: "training_date_initial",
                width: "130px",
            },
            {
                label: intl.get("Reports.Pharmacists.CreatedOn").d("User Created Date"),
                column_name: "created_on",
                width: "110px",
            },
            {
                label: intl.get("Reports.Pharmacists.TrainingDateLatest").d("Latest Training Date"),
                column_name: "training_date_latest",
                width: "140px",
            },
        ];

        // Add dynamic institution columns
        const institutionColumns = generateInstitutionColumns(maxInstitutions, formatters);

        return [...baseColumns, ...institutionColumns];
    }, [maxInstitutions, formatters]);

    const filterFields = [
        { name: "name", label: intl.get("Name").d("Name"), type: "text" },
        { name: "crf", label: intl.get("CRF").d("CRF"), type: "text" },
        {
            name: "crf_state",
            label: intl.get("State").d("State"),
            type: "multiselect",
            optionsKey: "crf_state",
        },
        { name: "institution_name", label: intl.get("Reports.Pharmacists.FilterInstitutionName").d("Institution Name"), type: "text" },
        { name: "institution_cnpj", label: intl.get("Reports.Pharmacists.FilterInstitutionCNPJ").d("Institution CNPJ"), type: "text" },
    ];

    // Don't render until we know max_institutions
    if (isLoading) {
        return null;
    }

    return (
        <ReportLayout
            title="Reports.Pharmacists.PageTitle"
            titleDefault="Dispensers Report"
            manualSlug="reports-pharmacists"
            tableUrl="/reports/pharmacists/table"
            filterOptionsUrl="/reports/pharmacists/filter-options"
            exportUrl="/reports/pharmacists/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.RegistrationDate").d("Registration Date")}
        />
    );
};

export default PharmacistsReportPage;
